import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Hammer, Home, Trash2, ArrowRight } from 'lucide-react';
import { services } from '../data/mock';
import { useLanguage } from '../context/languagecontext';
import { useAudience } from '../context/audiencecontext';

const iconMap = { Hammer, Home, Trash2 };

export const ServicesOverview = () => {
  const { t } = useLanguage();
  const { isCommercial } = useAudience();
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 1, 0.5, 1] } }
  };
  
  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4 uppercase tracking-wide">
            {isCommercial ? t('commercial.servicesHeader') : t('services.title')}
          </h2>
          <p className="font-body text-xl text-gray-600 max-w-3xl mx-auto">
            {isCommercial ? t('commercial.servicesSubtitle') : t('services.subtitle')}
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <motion.div key={service.id} variants={cardVariants} whileHover={{ y: -8, transition: { duration: 0.3 } }} className="group bg-white rounded-sm overflow-hidden border border-slate-200 hover:border-orange-500 transition-all duration-300 shadow-md hover:shadow-hard relative">
                <div className="absolute top-0 left-0 w-full h-1 bg-orange-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                <div className="relative h-56 overflow-hidden">
                  <img src={service.image} alt={t(`services.service${service.id}.title`)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 grayscale-[30%] group-hover:grayscale-0" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 w-16 h-16 bg-orange-500 rounded-sm flex items-center justify-center shadow-hard-sm group-hover:shadow-hard transition-all">
                    <IconComponent className="w-8 h-8 text-white" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3 uppercase tracking-wider">
                    {t(`services.service${service.id}.title`)}
                  </h3>
                  <p className="font-body text-gray-600 mb-6 leading-relaxed">
                    {t(`services.service${service.id}.description`)}
                  </p>
                  <Link to="/services" className="inline-flex items-center text-orange-500 hover:text-orange-600 font-bold uppercase tracking-wider group/link text-sm">
                    {t('services.learnMore')}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/link:translate-x-2 transition-transform" />
                  </Link>
                </div>
                <div className="absolute bottom-0 right-0 w-0 h-0 border-l-[30px] border-l-transparent border-b-[30px] border-b-slate-50 group-hover:border-b-orange-50 transition-colors"></div>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5, duration: 0.6 }} className="text-center mt-16">
          <Link to="/contact" className="inline-flex items-center px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wider rounded-sm shadow-hard transition-all active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            {t('services.cta')}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
